import type { CommunicationStyle, LicenseStatusResource, LicenseUsageResource } from "@octopusdeploy/octopus-server-client";
import { CommunicationStyleValues } from "@octopusdeploy/octopus-server-client";
interface DeploymentTargetLimitResult {
    isLimitReached: boolean;
    licensedLimit: number;
    currentUsage: number;
    isUnlimited: boolean;
    isPtm: boolean;
}
export const calculateDeploymentTargetLimits = (licenseUsage: LicenseUsageResource | null, licenseStatus: LicenseStatusResource | null): Map<CommunicationStyle, DeploymentTargetLimitResult> => {
    const limitsMap = new Map<CommunicationStyle, DeploymentTargetLimitResult>();
    if (!licenseUsage || !licenseStatus || !licenseUsage.Limits) {
        // Return empty limits for all communication styles
        Object.values(CommunicationStyleValues).forEach((communicationStyle) => {
            limitsMap.set(communicationStyle, {
                isLimitReached: false,
                licensedLimit: 0,
                currentUsage: 0,
                isUnlimited: false,
                isPtm: false,
            });
        });
        return limitsMap;
    }
    const isPtm = licenseStatus.IsPtm;
    const machinesLimit = licenseUsage.Limits.find((limit) => limit.Name.toLowerCase() === "machines");
    const targetsLimit = licenseUsage.Limits.find((limit) => limit.Name.toLowerCase() === "targets");
    Object.values(CommunicationStyleValues).forEach((communicationStyle) => {
        // Cloud Region (None communication style) is always unlimited
        if (communicationStyle === CommunicationStyleValues.None) {
            limitsMap.set(communicationStyle, {
                isLimitReached: false,
                licensedLimit: 0,
                currentUsage: 0,
                isUnlimited: true,
                isPtm,
            });
            return;
        }
        let applicableLimit = null;
        // Check if this communication style is covered by machines limit's TargetTypes
        if (isPtm && machinesLimit?.TargetTypes) {
            const communicationStyleLimitName = getCommunicationStyleLimitName(communicationStyle);
            const isTargetTypeSupported = machinesLimit.TargetTypes.some((targetType) => targetType.toLowerCase() === communicationStyleLimitName.toLowerCase());
            if (isTargetTypeSupported) {
                applicableLimit = machinesLimit;
            }
        }
        else {
            applicableLimit = targetsLimit;
        }
        if (applicableLimit) {
            limitsMap.set(communicationStyle, {
                isLimitReached: !applicableLimit.IsUnlimited && applicableLimit.CurrentUsage >= applicableLimit.LicensedLimit,
                licensedLimit: applicableLimit.LicensedLimit,
                currentUsage: applicableLimit.CurrentUsage,
                isUnlimited: applicableLimit.IsUnlimited,
                isPtm,
            });
        }
        else {
            limitsMap.set(communicationStyle, {
                isLimitReached: false,
                licensedLimit: 0,
                currentUsage: 0,
                isUnlimited: false,
                isPtm,
            });
        }
    });
    return limitsMap;
};
function getCommunicationStyleLimitName(communicationStyle: CommunicationStyle): string {
    // For most communication styles, the limit name is the same as the communication style value
    if (communicationStyle === CommunicationStyleValues.None) {
        return "targets";
    }
    return communicationStyle;
}
